/**
 * HR "preview as" state — an HR admin previewing the HR system as another
 * employee (read-only; the server gates it to HR admins and blocks writes).
 *
 * Persisted in localStorage so the axios interceptor can attach the
 * X-Impersonate-Employee-Id header, with a tiny pub/sub so React components
 * stay in sync via useSyncExternalStore.
 */
import { useSyncExternalStore } from 'react'

const ID_KEY = 'grm_hr_preview_employee_id'
const NAME_KEY = 'grm_hr_preview_employee_name'

export interface HrPreview {
  employeeId: string | null
  employeeName: string | null
}

const listeners = new Set<() => void>()
function emit() {
  for (const l of listeners) l()
}

export function getHrPreview(): HrPreview {
  return {
    employeeId: localStorage.getItem(ID_KEY),
    employeeName: localStorage.getItem(NAME_KEY),
  }
}

export function setHrPreview(employeeId: string, employeeName: string) {
  localStorage.setItem(ID_KEY, employeeId)
  localStorage.setItem(NAME_KEY, employeeName)
  emit()
}

export function clearHrPreview() {
  localStorage.removeItem(ID_KEY)
  localStorage.removeItem(NAME_KEY)
  emit()
}

// Cache a stable snapshot so useSyncExternalStore doesn't loop (it compares by
// reference); rebuild it only when the store changes.
let snapshot: HrPreview = getHrPreview()
function refreshSnapshot() {
  snapshot = getHrPreview()
}
listeners.add(refreshSnapshot)

function subscribe(cb: () => void) {
  listeners.add(cb)
  return () => listeners.delete(cb)
}
function getSnapshot(): HrPreview {
  return snapshot
}

/** React hook: current HR preview state (reactive). */
export function useHrPreview(): HrPreview {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
